import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BreadCrumbItem, BreadCrumbModule } from '@progress/kendo-angular-navigation';
import { homeIcon } from '@progress/kendo-svg-icons';
import { Subject, takeUntil } from 'rxjs';
import { CosmosCustomer } from '../shared/models/customer';
import { SecurityService } from '../shared/security/security.service';
import { KendoAlertService } from '../shared/services/kendo-alerts.service';

@Component({
    selector: 'ntw-routing-breadcrumbs',
    template: `
   @if (this.currentAccount) {
     <kendo-breadcrumb (itemClick)="onItemClick($event)" [items]="items"></kendo-breadcrumb>
   }
   `,
   imports: [BreadCrumbModule]
})
export class RoutingBreadCrumbsComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();

    public items: BreadCrumbItem[] = [];

    public currentAccount: CosmosCustomer;

    constructor(private router: Router,
        private translateService: TranslateService,
        private securityService: SecurityService,
        private kendoAlertService: KendoAlertService) {
    }

    ngOnInit(): void {
        this.setupLanguageChangeHandler();
        this.router.events.pipe(takeUntil(this._destroying$)).subscribe({
            next: () => {
                if (this.currentAccount) {
                    this.createBreadCrumbsFromRoute();
                }
            },
            error: error => console.error(error)
        });
        this.securityService.getCurrentAccount().pipe(takeUntil(this._destroying$)).subscribe({
            next: account => {
                this.currentAccount = account;
                if (!this.currentAccount) {
                    this.items = [];
                    return;
                }
                this.createBreadCrumbsFromRoute();
            },
            error: error => {
                this.kendoAlertService.showErrorAlert(error);
            }
        });
    }

    public ngOnDestroy(): void {
        this._destroying$.next(null);
        this._destroying$.complete();
    }

    private setupLanguageChangeHandler(): void {
        this.translateService.onLangChange.pipe(takeUntil(this._destroying$)).subscribe(() => {
            if (!this.currentAccount) {
                return;
            }
            this.createBreadCrumbsFromRoute();
        });
    }

    public onItemClick(item: BreadCrumbItem): void {
        const selectedItemIndex = this.items.findIndex(i => i.text === item.text);
        const urlSegments = this.replaceCustomerNameWithRoute(selectedItemIndex);
        this.router.navigate(urlSegments);
    }

    private createBreadCrumbsFromRoute(): void {
        let route: string | undefined = this.router.url;
        route = this.removeCustomerAxCodeFromRoute(route);
        const fragments = route?.split('/')
            .map((segment) => {
                switch (segment) {
                    case 'draftorder':
                        return {
                            text: this.translateService.instant('routing.draftOrder'),
                            title: segment
                        };
                    case 'openorders':
                        return {
                            text: this.translateService.instant('routing.openOrders'),
                            title: segment
                        };
                    case 'orders':
                        return {
                            text: this.translateService.instant('routing.placedOrders'),
                            title: segment
                        };
                    case 'invoices':
                        return {
                            text: this.translateService.instant('routing.invoices'),
                            title: segment
                        };
                    case 'products':
                        return {
                            text: this.translateService.instant('routing.products'),
                            title: segment
                        };
                    default:
                        return {
                            text: this.removeCompanyKeyFromString(segment),
                            title: segment
                        };
                }
            }) ?? [];
        this.items = [
            {
                text: this.currentAccount.CustomerName,
                title: this.currentAccount.CustomerName,
                svgIcon: homeIcon
            },
            ...fragments
        ];
    }

    private removeCustomerAxCodeFromRoute(route: string) {
        const pathSegments = route
            .split(/[?#]/, 1)[0]
            .split('/')
            .filter(Boolean);
        const accountSegmentIndex = pathSegments.findIndex(segment => segment.toLowerCase() === 'account');
        if (accountSegmentIndex < 0 || accountSegmentIndex + 1 >= pathSegments.length) {
            return undefined;
        }

        const remainingSegments = pathSegments.slice(accountSegmentIndex + 2);
        return remainingSegments.length > 0 ? remainingSegments.join('/') : undefined;
    }

    private replaceCustomerNameWithRoute(selectedItemIndex: number) {
        const accountRoute = `account/${this.currentAccount.AXCode}`;
        const urlParts = this.items.slice(0, selectedItemIndex + 1);

        const customerNameUrlPart = urlParts.find(x => x.title === this.currentAccount.CustomerName);
        if (customerNameUrlPart) {
            customerNameUrlPart.title = accountRoute;
        }

        return urlParts.map(x => x.title);
    }

    private removeCompanyKeyFromString(str: string): string {
        const keySeparator = "_";

        return !str.includes(keySeparator) ? str : str.slice(0, str.indexOf(keySeparator));
    }
}
